// Last gate before client serialization: pass typed user-facing errors through,
// scrub everything else. Recognizes BffUserFacingError (from the Python wire)
// and any local Error with a string `tag` field on the allowlist.
//
// The allowlist is DERIVED from the tag manifest
// (client/src/api/contracts/user-facing-error-tags.json) — the one statement of
// which tags may reach a user verbatim. To make a tag user-facing, add it to the
// manifest with a real raiser behind it (the drift test
// server/app/core/user_facing_tags_drift_test.py enforces that); never add
// entries here. Tags on the wire but absent from the manifest are wire-only:
// typed and matchable by BFF code, scrubbed before display.
//
// Design note — EXACT-MATCH, not prefix-based:
// Python error subclasses can append runtime suffixes to their base tags:
// RoleForbidden -> "role-forbidden:<capability>", NotFound -> "not-found:<kind>".
// Only the exact suffixed value passes through; a bare base tag does NOT cover
// its suffixed variants.

import { isNotFound, isRedirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";

import manifest from "@/api/contracts/user-facing-error-tags.json";
import { BffUserFacingError } from "@/lib/errors";

export const USER_FACING_ERROR_TAGS: ReadonlySet<string> = new Set(Object.keys(manifest.tags));

const GENERIC_MESSAGE =
  "Something went wrong on the server. Please try again — if the problem persists, contact support.";

// Pull the per-call X-Atlas-Request-Id off a caught InternalApiCallError so we can
// surface it to the user as a support "ref" and log it for grep correlation. Duck-typed
// (not `instanceof`) on purpose: InternalApiCallError lives in the server-only client.ts
// which imports node:crypto, and this module is client-bundled — see lib/errors.ts. Not
// every caught error originates from the internal-api transport, so absence is tolerated.
function requestIdOf(err: unknown): string | undefined {
  if (!(err instanceof Error)) return undefined;
  const requestId = (err as { requestId?: unknown }).requestId;
  return typeof requestId === "string" && requestId !== "" ? requestId : undefined;
}

// Showing the full uuid (not a truncated prefix) keeps the user-visible ref an exact,
// unambiguous match for the BFF and Python log lines.
function scrubbedMessage(requestId: string | undefined): string {
  if (!requestId) return GENERIC_MESSAGE;
  return `Something went wrong on the server (ref: ${requestId}). Please try again — if the problem persists, contact support and quote this reference.`;
}

function isUserFacingError(err: unknown): err is Error & { tag: string } {
  if (!(err instanceof Error)) return false;
  if (err instanceof BffUserFacingError) {
    return USER_FACING_ERROR_TAGS.has(err.tag);
  }
  const tag = (err as { tag?: unknown }).tag;
  return typeof tag === "string" && USER_FACING_ERROR_TAGS.has(tag);
}

/**
 * Decide what a failed server function is allowed to send to the client.
 * Router control-flow values (redirect / notFound) and whitelisted
 * user-facing errors pass through untouched; everything else is logged
 * server-side and replaced with a generic message. Returns the value the
 * caller should throw.
 */
export function toClientSafeError(err: unknown, label: string): unknown {
  if (isRedirect(err) || isNotFound(err)) return err;
  if (isUserFacingError(err)) return err;
  const requestId = requestIdOf(err);
  console.error(
    `[server-fn:${label}] internal error${requestId ? ` (ref: ${requestId})` : ""}`,
    err,
  );
  return new Error(scrubbedMessage(requestId));
}

// Registered globally in start.ts (first in functionMiddleware, so it wraps
// every other middleware and every handler). No per-function wrapper to
// forget: any server function added later is scrubbed automatically.
//
// That blanket coverage includes `.validator()` failures: a zod error thrown
// by a server function's validator is NOT user-facing and gets scrubbed to
// the generic message. If a form needs field-level feedback, validate
// client-side with the same schema, or throw a tagged `invalid-input:*`
// error from the handler.
export const safeErrorFunctionMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next, serverFnMeta }) => {
    try {
      return await next();
    } catch (err) {
      throw toClientSafeError(err, `${serverFnMeta.filename}:${serverFnMeta.name}`);
    }
  },
);
